<template>
  <form @submit.prevent="verifyAndSubmit" class="feedback-form" role="form">
    <div class="feedback-form__title">
      Your feedback helps us improve our courses.
    </div>
    <div class="feedback-form__content">
      <div class="feedback-form__group">
        <label class="feedback-form__label"
          >How would you rate this course?</label
        >
        <div class="feedback-form__star-wrapper">
          <StarRating
            :increment="1"
            :max-rating="5"
            :show-rating="false"
            :star-size="28"
            active-color="#ef7d22"
            v-model="contentRating"
            @rating-selected="updateRating"
            :read-only="(rating && rating.confirmed) || firstSave"
          />
        </div>
        <div class="feedback-form__limits">
          <div class="feedback-form__limits--min">Poor</div>
          <div class="feedback-form__limits--max">Great</div>
        </div>
      </div>
      <div class="feedback-form__group">
        <label class="feedback-form__label"
          >How would you rate your instructor <strong>{{ instructor }}</strong
          >?</label
        >
        <div class="feedback-form__star-wrapper">
          <StarRating
            :increment="1"
            :max-rating="5"
            :show-rating="false"
            :star-size="28"
            active-color="#ef7d22"
            v-model="instructorRating"
            @rating-selected="updateRating"
            :read-only="(rating && rating.confirmed) || firstSave"
          />
        </div>
        <div class="feedback-form__limits">
          <div class="feedback-form__limits--min">Poor</div>
          <div class="feedback-form__limits--max">Great</div>
        </div>
      </div>
      <div class="feedback-form__group">
        <div class="feedback-form__label">Comments</div>
        <textarea
          v-model="observations"
          @focus="submissionError = false"
          class="feedback-form__observations"
          rows="15"
          cols="47"
          @blur="updateRating"
          :disabled="(rating && rating.confirmed) || firstSave"
        ></textarea>
      </div>
      <div class="feedback-form__submit-btn-wrapper">
        <button
          v-if="!rating || (rating && !rating.confirmed)"
          :disabled="isPostingData || !requirementsMet"
          type="submit"
          class="btn btn--secondary btn--light"
        >
          Submit
        </button>
        <ActionLoader
          v-if="firstSave"
          name="ball-clip-rotate"
          loaderClass="feedback-form__submit-btn-wrapper__loading-spinner"
        />
      </div>
    </div>
  </form>
</template>

<script>
import StarRating from 'vue-star-rating'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'FeedbackForm',
  components: {
    StarRating,
    ActionLoader,
  },
  props: {
    course: {
      type: Object,
      required: true,
    },
    rating: {
      type: Object,
      default: () => {
        return {}
      },
    },
  },
  data() {
    return {
      submissionError: false,
      instructorRating: null,
      contentRating: null,
      observations: '',
      isPostingData: false,
      instructor: '',
      firstSave: false,
    }
  },
  computed: {
    requirementsMet() {
      const required = [this.contentRating, this.instructorRating]
      const missing = required.filter(requirement => !requirement)
      return missing.length === 0
    },
  },
  methods: {
    async updateRating() {
      const courseRating = {
        courseId: this.course.id,
        observations: this.observations,
        instructor_rating: this.instructorRating,
        course_rating: this.contentRating,
        instructor: this.course.instructors[0].id,
        confirmed: false,
      }
      if (this.rating && this.rating.id) {
        courseRating.id = this.rating.id
      } else {
        this.firstSave = true
      }
      await this.$store.dispatch('courses/saveCourseRating', courseRating)
      await this.$store.dispatch('courses/getCourseById', this.course.id)
      this.sendMixpanelEvent(this.eventsConstants.CONTENT_RATING_SENT, {
        course_id: this.course.id,
        course_name: this.course.name,
        instructor_id: this.course.instructors[0].id,
        instructor_name: this.course.instructors[0].name,
        ...courseRating,
      })
      this.firstSave = false
    },
    async verifyAndSubmit() {
      if (this.requirementsMet) {
        const courseRating = {
          courseId: this.course.id,
          observations: this.observations,
          instructor_rating: this.instructorRating,
          course_rating: this.contentRating,
          instructor: this.course.instructors[0].id,
          confirmed: true,
        }
        if (this.rating && this.rating.id) courseRating.id = this.rating.id
        this.isPostingData = true
        await this.$store.dispatch('courses/saveCourseRating', courseRating)
        await this.$store.dispatch('courses/getCourseById', this.course.id)
        this.isPostingData = false
        this.sendMixpanelEvent(this.eventsConstants.CONTENT_RATING_SENT, {
          course_id: this.course.id,
          course_name: this.course.name,
          instructor_id: this.course.instructors[0].id,
          instructor_name: this.course.instructors[0].name,
          ...courseRating,
        })
        this.$emit('feedback-submitted')
        this.$store.dispatch('app/setSuccess', 'Thank you for your feedback!')
      }
    },
  },
  async mounted() {
    this.contentRating =
      this.rating && this.rating.course_rating
        ? parseInt(this.rating.course_rating)
        : 0
    this.instructorRating =
      this.rating && this.rating.instructor_rating
        ? parseInt(this.rating.instructor_rating)
        : 0
    this.observations =
      this.rating && this.rating.observations ? this.rating.observations : ''
    let response = {}
    if (this.rating && this.rating.instructor) {
      response = await this.$store.dispatch(
        'courses/getInstructorById',
        this.rating.instructor
      )
    }
    this.instructor = response.name || this.course.instructors[0].name
  },
}
</script>

<style lang="scss" scoped>
.feedback-form {
  padding: 0 0 25px 0;
  color: var(--black);
  &__title {
    font-family: 'CircularStd-Bold';
    font-size: 17px;
    font-weight: 700;
    padding: 50px 25px 25px 25px;
    width: 100%;
    position: relative;
    @include tablet {
      padding-left: 30px;
      padding-right: 35px;
    }
    &::after {
      content: '';
      display: block;
      height: 1px;
      background: #b3b3b3;
      margin-top: 40px;
    }
  }
  &__content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding: 0 30px;
  }
  &__group {
    width: 100%;
    margin: 0 auto 25px auto;
  }
  &__label {
    font-weight: 500;
    font-size: 14px;
  }
  &__star-wrapper {
    margin: 10px 0 0 0;
    display: flex;
    justify-content: left;
  }
  &__limits {
    display: flex;
    justify-content: left;
    color: var(--gray);
    text-transform: uppercase;
    font-size: 10px;
    &--min {
      margin: 0 0 0 3px;
    }
    &--max {
      margin: 0 0 0 75px;
    }
  }
  &__observations {
    resize: none;
    width: 100%;
    height: 140px;
  }
  &__submit-btn-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    &__loading-spinner {
      margin-left: 10px;
    }
  }
}
</style>
